import React, { FC, useMemo, useState } from "react";
import { HiXMark } from "react-icons/hi2";
import { useParams } from "react-router-dom";
import { MultiValue } from "react-select";

import Button from "../../../components/button/Button";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import CustomSelect from "../../../components/select/Select";
import {
  HomeFilter,
  HomeTab,
} from "../../../factories/works/models/home-enums";
import { useHomeSearchParams } from "../../../hooks/useHomeSearchParams";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetStatusesQuery } from "../../../slices/StatusesSlice";
import { useGetUserQuery } from "../../../slices/UserSlice";
import {
  useGetFirmsWorksCountsQuery,
  useGetHomeWorksQuery,
} from "../../../slices/WorkSlice";
import { useGetWorkTypesQuery } from "../../../slices/WorkTypeSlice";
import { SelectOptionType } from "../../../types/types";
import { WorkList } from "../../works/components/WorkList";
import { AddWorkModal } from "../../works/modals/AddWork.modal";
import { WorkTableSort } from "../../works/types/work-table";
import { HomeStats } from "./HomeStats";

type HomeUserFilters = {
  workStatuses: SelectOptionType[];
  worktypeIds: SelectOptionType[];
};

const EMPTY_FILTERS: HomeUserFilters = {
  workStatuses: [],
  worktypeIds: [],
};

export const HomeUserWorks: FC = () => {
  const params = useParams<{ userId: string }>();
  const targetUserId = params?.userId || "";
  const orgId = getLinkedFirm()?.orgId || "";

  const { activeTab, activeFilter, page, sortBy, sortOrder, setPage, setSort } =
    useHomeSearchParams({
      defaultTab: HomeTab.ThisWeek,
      defaultSortBy: WorkTableSort.StartDate,
      defaultSortOrder: "desc",
    });

  const [filters, setFilters] = useState<HomeUserFilters>(EMPTY_FILTERS);
  const [isAddWork, setIsAddWork] = useState(false);
  const [searchWorks, setSearchWorks] = useState("");

  const canLoad = Boolean(orgId && targetUserId);

  const { data: user, isLoading: isLoadingUser } = useGetUserQuery(
    {
      userId: targetUserId,
      orgId,
    },
    {
      skip: !canLoad,
    },
  );

  const countsArgs = useMemo(
    () => ({
      orgId: orgId || "",
      userId: targetUserId || "",
      isMyWork: true,
    }),
    [orgId, targetUserId],
  );

  const {
    data: counts,
    isLoading: isLoadingCounts,
    isError: isCountsError,
  } = useGetFirmsWorksCountsQuery(countsArgs, {
    skip: !canLoad,
  });

  const statusLabels = useMemo(
    () => filters.workStatuses.map((item) => item.label),
    [filters.workStatuses],
  );

  const workTypeIds = useMemo(
    () => filters.worktypeIds.map((item) => String(item.value)),
    [filters.worktypeIds],
  );

  const dueDates =
    activeFilter === HomeFilter.OverDue ? HomeFilter.OverDue : activeTab;

  const worksArgs = useMemo(
    () => ({
      pageNumber: page,
      pageSize: 20,
      sortBy,
      sortOrder,
      worktypeIds: workTypeIds,
      dueDates,
      status: statusLabels,
      assignedUserId: targetUserId,
      orgId,
    }),
    [
      page,
      sortBy,
      sortOrder,
      workTypeIds,
      dueDates,
      statusLabels,
      targetUserId,
      orgId,
    ],
  );

  const { data: works = [], isLoading: isLoadingWorks } = useGetHomeWorksQuery(
    worksArgs,
    {
      skip: !canLoad,
    },
  );

  const { data: workTypes, isLoading: isLoadingWorkTypes } =
    useGetWorkTypesQuery(
      { orgId },
      {
        skip: !orgId,
      },
    );

  const { data: statuses, isLoading: isLoadingStatuses } = useGetStatusesQuery(
    { orgId },
    {
      skip: !orgId,
    },
  );

  const totalRecords = works?.[0]?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / 20));

  const workTypeOptions = useMemo<SelectOptionType[]>(() => {
    return (workTypes ?? []).map((item) => ({
      label: item.name,
      value: item.id,
    }));
  }, [workTypes]);

  const statusesOptions = useMemo<SelectOptionType[]>(() => {
    return (statuses ?? []).flatMap((item) => [
      {
        label: item?.name,
        value: item?.id,
        color: item?.color,
        isBold: true,
      },
      ...((item?.workTypeSubStatuses ?? []).map((subItem) => ({
        label: subItem?.subStatus,
        value: subItem?.id,
        color: item?.color,
      })) as SelectOptionType[]),
    ]);
  }, [statuses]);

  const hasActiveFilters =
    filters.workStatuses.length > 0 || filters.worktypeIds.length > 0;

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const handleWorkTypeChange = (
    newValue: MultiValue<SelectOptionType> | SelectOptionType,
  ) => {
    const value = Array.isArray(newValue)
      ? newValue
      : newValue
      ? [newValue]
      : [];

    setFilters((prev) => ({
      ...prev,
      worktypeIds: value,
    }));

    setPage(1);
  };

  const handleStatusChange = (
    newValue: MultiValue<SelectOptionType> | SelectOptionType,
  ) => {
    const value = Array.isArray(newValue)
      ? newValue
      : newValue
      ? [newValue]
      : [];

    setFilters((prev) => ({
      ...prev,
      workStatuses: value,
    }));

    setPage(1);
  };

  if (!canLoad) {
    return <LottieLoading />;
  }

  return (
    <>
      <div className={"grid h-full w-full grid-rows-[max-content_1fr] gap-6"}>
        <HomeStats
          isLoading={isLoadingCounts}
          isError={isCountsError}
          thisWeek={counts?.thisWeek?.total || 0}
          nextWeek={counts?.nextWeek?.total || 0}
          later={counts?.later?.total || 0}
          allOpen={counts?.allOpen?.total || 0}
          completed={counts?.completed || 0}
        />

        <WorkList
          works={works}
          statusFilter={statusLabels}
          isLoading={isLoadingWorks || isLoadingUser}
          hasPages
          page={page}
          totalPages={totalPages}
          total={totalRecords}
          pageSize={20}
          onPageChange={setPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={setSort}>
          <div
            className={
              "grid grid-cols-[1fr_max-content] items-start gap-2 px-8 pt-6"
            }>
            <div className={"flex items-center gap-4"}>
              <div className={"font-medium"}>
                {user?.userProfile?.firstName} {user?.userProfile?.lastName}
              </div>

              <div className={"flex items-end gap-4 pb-4"}>
                <div className={"w-[150px] min-w-[100px]"}>
                  <div className={"text-sm font-semibold text-gray-400"}>
                    Work Type
                  </div>
                  <CustomSelect
                    options={workTypeOptions}
                    value={filters.worktypeIds}
                    onChange={handleWorkTypeChange}
                    inputValue={searchWorks}
                    onChangeInput={setSearchWorks}
                    isMulti
                    isLoading={isLoadingWorkTypes}
                    withMenu
                    placeholder={"All"}
                    selectStyle={{
                      fitChips: true,
                      small: true,
                      width: "250px",
                      workTable: true,
                    }}
                  />
                </div>

                <div className={"w-[150px] min-w-[100px]"}>
                  <div className={"text-sm font-semibold text-gray-400"}>
                    Status
                  </div>
                  <CustomSelect
                    options={statusesOptions}
                    value={filters.workStatuses}
                    onChange={handleStatusChange}
                    isLoading={isLoadingStatuses}
                    placeholder={"All"}
                    withMenu
                    isMulti
                    selectStyle={{
                      fitChips: true,
                      small: true,
                      width: "250px",
                      workTable: true,
                    }}
                  />
                </div>

                {hasActiveFilters && (
                  <div className={"mt-4 flex items-center"}>
                    <Button
                      label={"Clear"}
                      onClick={clearFilters}
                      colorType={"ghost"}
                      icon={<HiXMark size={20} />}
                    />
                  </div>
                )}
              </div>
            </div>

            <Button
              buttonType={"button"}
              label={"Add work"}
              onClick={() => setIsAddWork(true)}
            />
          </div>
        </WorkList>
      </div>

      {isAddWork && (
        <AddWorkModal isOpen={isAddWork} onClose={() => setIsAddWork(false)} />
      )}
    </>
  );
};
